/* cali cart sync (2026-08-16, slimmed 2026-09-19)
 *
 * One reader of /cart.js for the whole cart footer. It listens to every way the
 * cart can change - Motion's own events plus a fetch/XHR net that catches the
 * app-driven mutations (Bag Builder, Selleasy, line removals) which fire no
 * theme event at all - debounces them, fetches the cart once, and:
 *
 *   1. rewrites "You're saving X" / "You're saving X in total", which Liquid
 *      renders outside Motion's ajax-refresh regions and would otherwise never
 *      update;
 *   2. broadcasts the fresh cart as `cali:cart`.
 *
 * WHAT WENT AWAY ON 2026-09-19 (the vendor did)
 * ---------------------------------------------
 * Until the in-house shipping-protection widget replaced Navidium ("Averra"),
 * this file also re-ran the vendor's `nvd_init`, swapped the in-cart protection
 * variant to whatever tier their code had just written into
 * localStorage.nvdVariant, de-duplicated their double add (their jQuery `.one()`
 * checkout handler fired once per matching ancestor of the click target), and
 * waited 1.2 s on load so their init could go first. All four existed to
 * contain someone else's code. With assets/cali-shipping-protection.js as the
 * single writer they have nothing left to contain and are gone; the initial
 * pass now runs immediately.
 *
 * This file no longer writes to the cart at all. It reads and it announces.
 */
(function () {
  'use strict';

  if (window.__caliCartSync) return;
  window.__caliCartSync = true;

  var PROTECTION_HANDLE = 'navidium-shipping-protection';
  var DEBOUNCE_MS = 600;
  var REBUILD_TIMEOUT_MS = 8000;
  var CART_MUTATION = /\/cart\/(add|change|update|clear)/;

  var scriptEl = document.getElementById('CaliCartSync') || document.currentScript;
  var moneySample = (scriptEl && scriptEl.getAttribute('data-money-sample')) || '';

  /* ---- i18n ----
     The same sentence is rendered by Liquid in snippets/cart-drawer.liquid and
     sections/main-cart.liquid from the locale keys
     bag_builder.cart_savings_prefix / bag_builder.cart_savings_total_suffix.
     Read the SAME two values off the tag so this rewrite cannot revert a
     translated cart to English. Falls back to today's English when the
     attribute is absent or the key is unregistered. */
  function attrStr(name, fallback) {
    var v = scriptEl && scriptEl.getAttribute(name);
    if (!v || v.indexOf('Translation missing') !== -1) return fallback;
    return v;
  }
  var savingsPrefix = attrStr('data-savings-prefix', "You're saving ");
  var savingsTotalSuffix = attrStr('data-savings-total-suffix', ' in total');

  /* ---- money formatting ----
     The snippet embeds a server-rendered sample of 123456789 cents
     (1,234,567.89 units) in the visitor's presentment currency. Parsing it
     recovers prefix, suffix, thousands and decimal separators, so output
     matches Liquid's `| money` exactly for every market. Exposed on
     window.caliCartSync because the shipping-protection fee label has to print
     the same money as the Liquid that painted it. */
  var moneyPattern = null;
  (function () {
    if (!moneySample) return;
    var m = moneySample.match(/1(\D*)234\D*56[78](\D*89)?/);
    if (!m) return;
    moneyPattern = {
      prefix: moneySample.slice(0, m.index),
      suffix: moneySample.slice(m.index + m[0].length),
      thousands: m[1] || '',
      decimal: m[2] ? m[2].slice(0, -2) : null
    };
  })();

  function groupDigits(intStr, sep) {
    return sep ? intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep) : intStr;
  }

  function formatCents(cents) {
    cents = Math.abs(Math.round(cents));
    var p = moneyPattern;
    if (p) {
      if (p.decimal === null) {
        return p.prefix + groupDigits(String(Math.round(cents / 100)), p.thousands) + p.suffix;
      }
      var intPart = String(Math.floor(cents / 100));
      var frac = String(cents % 100);
      if (frac.length < 2) frac = '0' + frac;
      return p.prefix + groupDigits(intPart, p.thousands) + p.decimal + frac + p.suffix;
    }
    try {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      }
    } catch (err) {}
    return (cents / 100).toFixed(2);
  }

  /* ---- cart helpers ---- */
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function isProtection(item) {
    return !!(item && item.handle && item.handle.indexOf(PROTECTION_HANDLE) !== -1);
  }

  /* ---- "You're saving X" ----
     Mirrors the Liquid in cart-drawer.liquid and main-cart.liquid:
     sum of _saved_price * quantity over lines carrying the property.
     Drawer shows it only with 2+ saved lines (a single built bag already
     has its own per-item pill); the cart page shows it whenever > 0. */
  function updateSavings(cart) {
    var cents = 0;
    var lines = 0;
    (cart.items || []).forEach(function (it) {
      if (isProtection(it)) return;
      var saved = it.properties && parseFloat(it.properties._saved_price);
      if (saved > 0) {
        cents += Math.round(saved * 100) * it.quantity;
        lines += 1;
      }
    });

    var wrappers = document.querySelectorAll('.cart__price-wrapper');
    Array.prototype.forEach.call(wrappers, function (wrap) {
      if (!wrap.querySelector('[data-subtotal]')) return;
      var inDrawer = !!(wrap.closest && wrap.closest('#CartDrawer'));
      var show = cents > 0 && (inDrawer ? lines > 1 : true);
      var el = wrap.querySelector('.cart__savings-text');
      if (!show) {
        if (el && el.parentNode) el.parentNode.removeChild(el);
        return;
      }
      if (!el) {
        el = document.createElement('div');
        el.className = 'cart__savings-text';
        wrap.appendChild(el);
      }
      var text = savingsPrefix + formatCents(cents) + (inDrawer ? savingsTotalSuffix : '');
      if (el.textContent !== text) el.textContent = text;
    });
  }

  /* ---- pipeline ---- */
  var syncTimer = null;
  var mutationTail = Promise.resolve();
  var pendingMutations = 0;
  var checkoutWasDisabled = new WeakMap();
  var expressWasInert = new WeakMap();
  var checkoutObserver = null;
  var checkoutFallback = null;
  var awaitingRebuild = false;
  var rebuildRevision = 0;
  var markupBeforeMutation = [];

  function cartProductNodes() {
    var drawer = document.querySelector('#CartDrawerForm [data-products]');
    var page = document.querySelector('#CartPageForm [data-products]');
    if (page || drawer) return [page, drawer].filter(Boolean);
    var other = document.querySelector('[data-products]');
    return other ? [other] : [];
  }

  function checkoutButtons() {
    return document.querySelectorAll('button[name="checkout"]');
  }

  function holdCheckout() {
    if (pendingMutations === 1) {
      markupBeforeMutation = cartProductNodes().map(function (products) { return products.innerHTML; });
    }
    awaitingRebuild = true;
    rebuildRevision += 1;
    if (checkoutObserver) checkoutObserver.disconnect();
    checkoutObserver = null;
    if (checkoutFallback) clearTimeout(checkoutFallback);
    checkoutFallback = null;
    Array.prototype.forEach.call(checkoutButtons(), function (button) {
      if (!checkoutWasDisabled.has(button)) checkoutWasDisabled.set(button, button.disabled);
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    Array.prototype.forEach.call(document.querySelectorAll('.additional-checkout-buttons'), function (buttons) {
      if (!expressWasInert.has(buttons)) expressWasInert.set(buttons, !!buttons.inert);
      buttons.inert = true;
      buttons.setAttribute('aria-busy', 'true');
    });
  }

  function releaseCheckout() {
    if (pendingMutations) return;
    awaitingRebuild = false;
    if (checkoutObserver) checkoutObserver.disconnect();
    checkoutObserver = null;
    if (checkoutFallback) clearTimeout(checkoutFallback);
    checkoutFallback = null;
    Array.prototype.forEach.call(checkoutButtons(), function (button) {
      if (!checkoutWasDisabled.has(button)) return;
      button.disabled = checkoutWasDisabled.get(button);
      checkoutWasDisabled.delete(button);
      button.removeAttribute('aria-busy');
    });
    Array.prototype.forEach.call(document.querySelectorAll('.additional-checkout-buttons'), function (buttons) {
      if (!expressWasInert.has(buttons)) return;
      buttons.inert = expressWasInert.get(buttons);
      expressWasInert.delete(buttons);
      buttons.removeAttribute('aria-busy');
    });
  }

  function finishMutation() {
    pendingMutations -= 1;
    if (pendingMutations) return;
    var products = cartProductNodes();
    var productsForms = products.map(function (node) { return node.closest('form'); });
    var revision = rebuildRevision;
    var sawRebuild = products.map(function (node, index) { return node.innerHTML !== markupBeforeMutation[index]; });
    function currentProducts(index) { return productsForms[index] ? productsForms[index].querySelector('[data-products]') : products[index]; }
    function rowsMatch(cart) {
      if (!products.length || !cart || !cart.items) return false;
      var expected = cart.items.map(function (item) { return [item.key, Number(item.quantity)]; }).sort(function (a, b) { return a[0].localeCompare(b[0]); });
      return products.every(function (_node, index) {
        var current = currentProducts(index);
        if (!current) return false;
        var actual = Array.prototype.map.call(current.querySelectorAll('[data-key]'), function (row) {
          var input = row.querySelector('input[name="updates[]"]');
          return [row.getAttribute('data-key'), input ? Number(input.value) : NaN];
        }).sort(function (a, b) { return a[0].localeCompare(b[0]); });
        return expected.length === actual.length && expected.every(function (item, rowIndex) { return item[0] === actual[rowIndex][0] && item[1] === actual[rowIndex][1]; });
      });
    }
    function verifyRebuild() {
      getCart().then(function (cart) {
        if (revision === rebuildRevision && !pendingMutations && sawRebuild.every(Boolean) && rowsMatch(cart)) releaseCheckout();
      }).catch(function () {});
    }
    if (products.length && window.MutationObserver) {
      checkoutObserver = new MutationObserver(function (records) {
        products.forEach(function (_node, index) {
          var current = currentProducts(index);
          if (records.some(function (record) {
            if (current && (record.target === current || current.contains(record.target))) return true;
            if (record.type !== 'childList' || record.target !== productsForms[index]) return false;
            return Array.prototype.some.call(record.addedNodes, function (node) { return node.nodeType === 1 && (node.matches('[data-products]') || node.querySelector('[data-products]')); });
          })) sawRebuild[index] = true;
        });
        if (sawRebuild.every(Boolean)) verifyRebuild();
      });
      // Motion can replace a products node, so observe each form as well as
      // descendant row changes; footer-only changes are ignored above.
      products.forEach(function (node, index) { checkoutObserver.observe(productsForms[index] || node, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-key'] }); });
    }
    checkoutFallback = setTimeout(function () {
      if (!awaitingRebuild || revision !== rebuildRevision) return;
      // A Motion rebuild can complete before the observer starts or replace
      // its node without another mutation. Re-read both sides before reloading.
      var settled = false;
      var readTimeout = setTimeout(function () { if (!settled && awaitingRebuild && revision === rebuildRevision) window.location.reload(); }, 3000);
      getCart().then(function (cart) {
        settled = true; clearTimeout(readTimeout);
        if (!awaitingRebuild || revision !== rebuildRevision || pendingMutations) return;
        if (sawRebuild.every(Boolean) && rowsMatch(cart)) releaseCheckout();
        else window.location.reload();
      }, function () { settled = true; clearTimeout(readTimeout); if (awaitingRebuild && revision === rebuildRevision) window.location.reload(); });
    }, REBUILD_TIMEOUT_MS);
    try { document.dispatchEvent(new CustomEvent('cart:build')); } catch (err) {}
    verifyRebuild();
    schedule();
  }

  function runMutation(fn) {
    pendingMutations += 1;
    holdCheckout();
    var result = mutationTail.then(function () {
      if (navigator.locks && navigator.locks.request) return navigator.locks.request('cali-cart-mutation', fn);
      return fn();
    });
    mutationTail = result.then(function () {}, function () {});
    return result.then(function (value) {
      finishMutation();
      return value;
    }, function (error) {
      finishMutation();
      throw error;
    });
  }

  document.addEventListener('click', function (event) {
    if (!awaitingRebuild) return;
    var target = event.target && event.target.closest && event.target.closest('button[name="checkout"], .cart__checkout, a[href$="/checkout"], a[href*="/checkout?"], .additional-checkout-buttons');
    if (target) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);
  document.addEventListener('submit', function (event) {
    if (!awaitingRebuild) return;
    if (event.target && event.target.querySelector && event.target.querySelector('[name="checkout"]')) { event.preventDefault(); event.stopImmediatePropagation(); }
  }, true);

  function schedule() {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(runSync, DEBOUNCE_MS);
  }

  function announce(cart) {
    try {
      document.dispatchEvent(new CustomEvent('cali:cart', { detail: { cart: cart } }));
    } catch (err) {}
  }

  function runSync() {
    syncTimer = null;
    getCart()
      .then(function (cart) {
        if (!cart || !cart.items) return;
        updateSavings(cart);
        announce(cart);
      })
      .catch(function () {});
  }

  /* ---- triggers ----
     Motion's own events plus a fetch/XHR net for app-driven mutations
     (same pattern as cali-shipping-bar.js; both patches chain safely).
     Our own protection writes go through this net too, which is how the widget
     sees the result of its write and settles. */
  document.addEventListener('cart:updated', schedule);
  document.addEventListener('ajaxProduct:added', schedule);
  document.addEventListener('cart:open', schedule);

  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var promise = origFetch.apply(this, arguments);
      if (CART_MUTATION.test(url)) {
        promise.then(schedule, function () {});
      }
      return promise;
    };
  }

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (url && CART_MUTATION.test(String(url))) {
      this.addEventListener('load', schedule);
    }
    return origOpen.apply(this, arguments);
  };

  window.caliCartSync = {
    formatCents: formatCents,
    getCart: getCart,
    isProtection: isProtection,
    refresh: schedule,
    runMutation: runMutation,
    isMutating: function () { return pendingMutations > 0; }
  };

  /* Initial pass. Heals a cart persisted from a previous session whose
     protection tier no longer matches its contents, and gives the widget its
     first `cali:cart` without a second /cart.js fetch of its own. */
  runSync();
})();
